import { PlatformError } from '@djgg/core';

/** Supported recipe controls. Safety, runtime and playability gates are mandatory. */
export interface BuildPipelineConfig {
  /** Server-owned closed compiler selection, never inferred from a prompt. */
  assembly?: 'courier-short@1' | 'maintained-profiles@1';
  /** Omitted on historical revisions; only explicit core-first enables staged construction. */
  construction?: 'whole-game' | 'core-first' | 'verified-core' | 'measured-first-playable';
  artwork: 'parallel' | 'after-playable' | 'after-core' | 'none';
  maxRepairRounds: number;
  diagnosis: boolean;
  qualityPasses: number;
  qualityBar: number;
  instructions: string;
  /** Optional adaptive controller. Historical recipes intentionally omit it. */
  adaptiveRepair?: {
    maxWallTimeMinutes: number;
    maxProviderCalls: number;
    maxCostUsd: number;
    stagnationRounds: number;
  };
}
export interface BuildPipelineRef {
  id: string;
  revision: number;
  stage?: 'standard' | 'prototype' | 'artwork';
}
export const LEGACY_BUILD_PIPELINE_REF: Readonly<BuildPipelineRef> = { id: 'current', revision: 1 };
/** One server-owned recipe for every newly created game. */
export const DEFAULT_BUILD_PIPELINE_REF: Readonly<BuildPipelineRef> = { id: 'measured-first-playable', revision: 3 };
/** Dormant until the consumer-first rollout and authorized global cutover. */
export const VERIFIED_CORE_PIPELINE_REF: Readonly<BuildPipelineRef> = { id: 'verified-core', revision: 1 };
export const MEASURED_FIRST_PLAYABLE_PIPELINE_REF: Readonly<BuildPipelineRef> = { id: 'measured-first-playable', revision: 3 };

/** Only the accepted identity travels back in requests; snapshot metadata is server-owned. */
export function buildPipelineRef(snapshot?: BuildPipelineSnapshot): BuildPipelineRef {
  return snapshot
    ? { id: snapshot.id, revision: snapshot.revision, stage: snapshot.stage }
    : { ...LEGACY_BUILD_PIPELINE_REF };
}

/** Adding artwork is one explicit pass; later iterations use the completed recipe. */
export function nextBuildPipelineRef(snapshot?: BuildPipelineSnapshot): BuildPipelineRef {
  const ref = buildPipelineRef(snapshot);
  return snapshot?.config?.artwork === 'after-playable' && snapshot.stage === 'artwork'
    ? { ...ref, stage: 'standard' }
    : ref;
}

export interface BuildPipelineRevision {
  revision: number;
  name: string;
  description: string;
  config: BuildPipelineConfig | null;
  createdAt: string;
  createdBy: string;
}
export interface BuildPipelineEntry {
  id: string;
  hidden: boolean;
  revisions: BuildPipelineRevision[];
}
export interface BuildPipelineCatalog { entries: BuildPipelineEntry[] }
export interface BuildPipelineSnapshot extends BuildPipelineRevision {
  schemaVersion: 1;
  id: string;
  stage: NonNullable<BuildPipelineRef['stage']>;
}
export interface PublishBuildPipelineInput {
  id: string;
  expectedRevision: number;
  name: string;
  description: string;
  config: BuildPipelineConfig;
}
export const DEFAULT_PIPELINE_CONFIG: BuildPipelineConfig = {
  artwork: 'after-playable', maxRepairRounds: 4, diagnosis: true,
  qualityPasses: 1, qualityBar: 0.7, instructions: '',
};
const seedMetadata = { revision: 1, createdAt: '2026-09-07T00:00:00.000Z', createdBy: 'system' };
/** Current is deliberately config:null: preserve the production path and its platform settings. */
export function defaultBuildPipelineCatalog(): BuildPipelineCatalog {
  return { entries: [
    { id: 'current', hidden: false, revisions: [{ ...seedMetadata, name: 'Current pipeline', description: 'The existing Studio build process.', config: null }] },
    { id: 'code-first', hidden: false, revisions: [{ ...seedMetadata, name: 'Code first, artwork later', description: 'Build and play a coded prototype, then add artwork when ready.', config: { ...DEFAULT_PIPELINE_CONFIG } }] },
    coreFirstSeed(),
    verifiedCoreSeed(),
    measuredFirstPlayableSeed(),
  ] };
}
function measuredFirstPlayableSeed(): BuildPipelineEntry {
  const revision1: BuildPipelineRevision = { ...seedMetadata, createdAt: '2026-09-30T00:00:00.000Z',
    name: 'Measured first playable', description: 'Author the complete playable loop first, retain every candidate, and adapt repairs while measurable progress continues.',
    config: {
      ...DEFAULT_PIPELINE_CONFIG,
      construction: 'measured-first-playable',
      artwork: 'parallel',
      maxRepairRounds: 12,
      adaptiveRepair: { maxWallTimeMinutes: 60, maxProviderCalls: 20, maxCostUsd: 15, stagnationRounds: 2 },
      instructions: `COMPLETE PLAYABLE FIRST. The first candidate must implement every critical gameplay verb, the complete objective path, win, loss, restart, and both desktop and native-touch interaction scenarios. Defer only incidental variety and polish. Every critical scenario must prove an independently observable effect against a seed-matched idle-negative control.`,
    },
  };
  const revision2: BuildPipelineRevision = {
    ...revision1,
    revision: 2,
    createdAt: '2026-10-01T00:00:00.000Z',
    description: 'Durably resume the measured playable loop within one accepted 20-minute end-to-end budget.',
    config: {
      ...revision1.config!,
      adaptiveRepair: {
        ...revision1.config!.adaptiveRepair!,
        maxWallTimeMinutes: 20,
        maxProviderCalls: 40,
      },
    },
  };
  const revision3: BuildPipelineRevision = {
    ...revision2,
    revision: 3,
    createdAt: '2026-10-06T00:00:00.000Z',
    name: 'Complete game creation',
    description: 'Complete gameplay and required artwork within one durable 20-minute build, retaining milestones for recovery.',
    config: {
      ...revision2.config!,
      instructions: `${revision2.config!.instructions} COMPLETION REQUIREMENTS: implement the reviewed objective through successful completion, defeat and clean restart. Positive score alone is not mission success. Implement createBot using ordinary commands to finish the full objective within 9000 simulation ticks; assign the primary seat as winner only after every required objective is complete. Tests must assert the concrete objective counts, successful extraction or equivalent ending, idle defeat, and clean restart. Every promised visual and animation must be bound to its final compatible asset. Preserve working controls and objective evidence during repairs; never remove a requirement to obtain a passing result.`,
    },
  };
  const revision4: BuildPipelineRevision = {
    ...revision3, revision: 4, createdAt: '2026-10-08T00:00:00.000Z',
    name: 'Maintained courier composition',
    description: 'Internal provider-free courier profile; admission requires immutable composition authority.',
    config: { ...revision3.config!, assembly: 'courier-short@1', artwork: 'none', maxRepairRounds: 0,
      diagnosis: false, qualityPasses: 1 },
  };
  const revision5: BuildPipelineRevision = {
    ...revision4, revision: 5, createdAt: '2026-10-11T00:00:00.000Z',
    name: 'Maintained game composition', description: 'Closed maintained profiles selected by immutable accepted intent.',
    config: { ...revision4.config!, assembly: 'maintained-profiles@1' },
  };
  return { id: 'measured-first-playable', hidden: true, revisions: [revision1, revision2, revision3, revision4, revision5] };
}
function coreFirstSeed(): BuildPipelineEntry {
  return { id: 'core-first', hidden: false, revisions: [{ ...seedMetadata, createdAt: '2026-09-08T00:00:00.000Z',
    name: 'Core gameplay first', description: 'Validate core mechanics in one playable section, then expand the full game. Add artwork when ready.',
    config: { ...DEFAULT_PIPELINE_CONFIG, construction: 'core-first' },
  }] };
}
function verifiedCoreSeed(): BuildPipelineEntry {
  return { id: 'verified-core', hidden: true, revisions: [{ ...seedMetadata, createdAt: '2026-09-25T00:00:00.000Z',
    name: 'Verified core', description: 'Finalize a supported quality contract, verify core mechanics, then release bounded expansion and media before one exact final audit.',
    config: { ...DEFAULT_PIPELINE_CONFIG, construction: 'verified-core', artwork: 'after-core' },
  }] };
}
/** Add newly shipped experiments without restoring older choices or rewriting revisions.
 * Retirement uses hidden entries, which always win over shipped seeds. */
export function withBuildPipelineSeeds(stored: BuildPipelineCatalog | null | undefined): BuildPipelineCatalog {
  if (!stored) return defaultBuildPipelineCatalog();
  const catalog = structuredClone(stored);
  if (!catalog.entries.some(entry => entry.id === 'core-first')) catalog.entries.push(coreFirstSeed());
  if (!catalog.entries.some(entry => entry.id === 'verified-core')) catalog.entries.push(verifiedCoreSeed());
  const measured = catalog.entries.find(entry => entry.id === 'measured-first-playable');
  if (!measured) catalog.entries.push(measuredFirstPlayableSeed());
  else for (const seed of measuredFirstPlayableSeed().revisions.filter(revision => revision.revision >= 2)) {
    if (!measured.revisions.some(revision => revision.revision === seed.revision)) measured.revisions.push(seed);
  }
  return catalog;
}
export function validatePipelineConfig(value: unknown): BuildPipelineConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new PlatformError('validation', 'Invalid pipeline recipe');
  const v = value as Record<string, unknown>;
  const adaptive = v.adaptiveRepair as Record<string, unknown> | undefined;
  if (Object.keys(v).some(k => k !== 'assembly' && k !== 'construction' && k !== 'adaptiveRepair' && !Object.hasOwn(DEFAULT_PIPELINE_CONFIG, k)) ||
      (v.assembly !== undefined && (!['courier-short@1', 'maintained-profiles@1'].includes(String(v.assembly)) || v.construction !== 'measured-first-playable'
        || v.artwork !== 'none' || v.maxRepairRounds !== 0 || v.diagnosis !== false || v.qualityPasses !== 1)) ||
      (v.construction !== undefined && v.construction !== 'whole-game' && v.construction !== 'core-first' && v.construction !== 'verified-core' && v.construction !== 'measured-first-playable') ||
      (v.construction === 'core-first' && v.artwork === 'parallel') ||
      (v.construction === 'verified-core' && v.artwork !== 'after-core') ||
      (v.artwork === 'after-core' && v.construction !== 'verified-core') ||
      !['parallel', 'after-playable', 'after-core', 'none'].includes(String(v.artwork)) ||
      !Number.isInteger(v.maxRepairRounds) || Number(v.maxRepairRounds) < 0 || Number(v.maxRepairRounds) > (v.construction === 'measured-first-playable' ? 12 : 6) ||
      typeof v.diagnosis !== 'boolean' || !Number.isInteger(v.qualityPasses) || Number(v.qualityPasses) < 1 || Number(v.qualityPasses) > 3 ||
      typeof v.qualityBar !== 'number' || !Number.isFinite(v.qualityBar) || v.qualityBar < 0 || v.qualityBar > 1 ||
      typeof v.instructions !== 'string' || v.instructions.length > 4000 ||
      (adaptive !== undefined && (
        !adaptive || typeof adaptive !== 'object' || Array.isArray(adaptive) ||
        Object.keys(adaptive).some(k => !['maxWallTimeMinutes', 'maxProviderCalls', 'maxCostUsd', 'stagnationRounds'].includes(k)) ||
        !Number.isInteger(adaptive.maxWallTimeMinutes) || Number(adaptive.maxWallTimeMinutes) < 1 || Number(adaptive.maxWallTimeMinutes) > 120 ||
        !Number.isInteger(adaptive.maxProviderCalls) || Number(adaptive.maxProviderCalls) < 1 || Number(adaptive.maxProviderCalls) > 40 ||
        typeof adaptive.maxCostUsd !== 'number' || !Number.isFinite(adaptive.maxCostUsd) || Number(adaptive.maxCostUsd) <= 0 || Number(adaptive.maxCostUsd) > 50 ||
        !Number.isInteger(adaptive.stagnationRounds) || Number(adaptive.stagnationRounds) < 1 || Number(adaptive.stagnationRounds) > 5
      ))) {
    throw new PlatformError('validation', 'Invalid pipeline recipe: use supported stages and bounded settings');
  }
  return structuredClone(v) as unknown as BuildPipelineConfig;
}

/** Server-owned fresh-create recipe; historical catalog visibility cannot reroute new games. */
export function measuredFirstPlayablePipelineSnapshot(catalog: BuildPipelineCatalog): BuildPipelineSnapshot {
  const entry = catalog.entries.find(candidate => candidate.id === MEASURED_FIRST_PLAYABLE_PIPELINE_REF.id);
  const revision = entry?.revisions.find(candidate => candidate.revision === MEASURED_FIRST_PLAYABLE_PIPELINE_REF.revision);
  if (!entry || !revision) throw new PlatformError('validation', 'Measured-first-playable pipeline revision is unavailable');
  const config = validatePipelineConfig(revision.config);
  return { ...structuredClone(revision), config, schemaVersion: 1, id: entry.id, stage: 'standard' };
}

export function isDurableMeasuredRevision(revision: number | undefined): revision is 2 | 3 | 4 | 5 {
  return revision === 2 || revision === 3 || revision === 4 || revision === 5;
}
export function isCompleteMeasuredRevision(revision: number | undefined): boolean {
  return revision === 3 || revision === 4 || revision === 5;
}
export function composedCourierPipelineSnapshot(catalog: BuildPipelineCatalog): BuildPipelineSnapshot {
  const entry = catalog.entries.find(candidate => candidate.id === 'measured-first-playable');
  const revision = entry?.revisions.find(candidate => candidate.revision === 4);
  if (!entry || !revision) throw new PlatformError('validation', 'Composed courier recipe is unavailable');
  const config = validatePipelineConfig(revision.config);
  if (config.assembly !== 'courier-short@1') throw new PlatformError('validation', 'Composed courier recipe identity changed');
  return { ...structuredClone(revision), config, schemaVersion: 1, id: entry.id, stage: 'standard' };
}

/** Revision 4 remains the immutable courier recipe. Revision 5 is the same
 * measured worker with a closed profile registry, never a mutable fallback. */
export function composedMaintainedPipelineSnapshot(catalog: BuildPipelineCatalog): BuildPipelineSnapshot {
  const entry = catalog.entries.find(candidate => candidate.id === 'measured-first-playable');
  const revision = entry?.revisions.find(candidate => candidate.revision === 5);
  if (!entry || !revision) throw new PlatformError('validation', 'Maintained composition recipe is unavailable');
  const config = validatePipelineConfig(revision.config);
  if (config.assembly !== 'maintained-profiles@1') throw new PlatformError('validation', 'Maintained composition recipe identity changed');
  return { ...structuredClone(revision), config, schemaVersion: 1, id: entry.id, stage: 'standard' };
}
export function isComposedPipeline(snapshot: BuildPipelineSnapshot | undefined): boolean {
  return snapshot?.id === 'measured-first-playable'
    && (snapshot.revision === 4 && snapshot.config?.assembly === 'courier-short@1'
      || snapshot.revision === 5 && snapshot.config?.assembly === 'maintained-profiles@1');
}

/** Server-owned lookup for controlled local/staging cohorts. It intentionally
 * bypasses picker visibility but never changes the default or global minimum. */
export function verifiedCorePipelineSnapshot(catalog: BuildPipelineCatalog): BuildPipelineSnapshot {
  const entry = catalog.entries.find(candidate => candidate.id === VERIFIED_CORE_PIPELINE_REF.id);
  const revision = entry?.revisions.find(candidate => candidate.revision === VERIFIED_CORE_PIPELINE_REF.revision);
  if (!entry || !revision) throw new PlatformError('validation', 'Verified-core pipeline revision is unavailable');
  const config = validatePipelineConfig(revision.config);
  return { ...structuredClone(revision), config, schemaVersion: 1, id: entry.id, stage: 'standard' };
}
export function validateBuildPipelineRef(value: unknown): BuildPipelineRef {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new PlatformError('validation', 'Invalid pipeline selection');
  const v = value as Record<string, unknown>;
  if (Object.keys(v).some(k => !['id', 'revision', 'stage'].includes(k)) || typeof v.id !== 'string' ||
      !/^[a-z][a-z0-9-]{0,63}$/.test(v.id) || !Number.isInteger(v.revision) || Number(v.revision) < 1 ||
      (v.stage !== undefined && !['standard', 'prototype', 'artwork'].includes(String(v.stage)))) {
    throw new PlatformError('validation', 'Invalid pipeline selection');
  }
  return { id: v.id, revision: Number(v.revision), ...(v.stage ? { stage: v.stage as BuildPipelineRef['stage'] } : {}) };
}
/** Resolve under acceptance using the game-owned pin. The caller separately requires live source for artwork. */
export function resolveBuildPipeline(catalog: BuildPipelineCatalog, requested?: BuildPipelineRef, active?: BuildPipelineSnapshot): BuildPipelineSnapshot {
  const ref = requested === undefined ? nextBuildPipelineRef(active) : validateBuildPipelineRef(requested);
  const entry = catalog.entries.find(p => p.id === ref.id);
  const revision = entry?.revisions.find(r => r.revision === ref.revision);
  if (!entry || !revision) throw new PlatformError('validation', 'Unknown pipeline revision');
  const continuing = active?.id === ref.id && active.revision === ref.revision;
  if (entry.hidden && !continuing) throw new PlatformError('validation', 'This pipeline is hidden from new selections');
  const config = revision.config === null ? null : validatePipelineConfig(revision.config);
  let stage: BuildPipelineSnapshot['stage'] = config?.assembly ? 'standard' : config?.artwork === 'after-playable' || config?.artwork === 'none' ? 'prototype' : 'standard';
  if (continuing && ['artwork', 'standard'].includes(active.stage) && config?.artwork === 'after-playable') stage = 'standard';
  if (ref.stage === 'artwork') {
    if (!continuing || config?.artwork !== 'after-playable' || !['prototype', 'artwork'].includes(active.stage)) {
      throw new PlatformError('validation', 'Add artwork requires a playable prototype from this pipeline revision');
    }
    stage = 'artwork';
  } else if (ref.stage !== undefined && ref.stage !== stage) {
    throw new PlatformError('validation', 'This stage is not supported by the selected pipeline');
  }
  return { ...structuredClone(revision), config, schemaVersion: 1, id: entry.id, stage };
}
export function isCodeOnlyPipeline(snapshot?: BuildPipelineSnapshot): boolean {
  return snapshot?.stage === 'prototype' || isComposedPipeline(snapshot);
}

/** Unseeded private route: cannot be selected from the ordinary recipe catalog. */
export function compositionPrivatePipelineSnapshotV2(): BuildPipelineSnapshot {
  return { schemaVersion: 1, id: 'composition-private-v2', revision: 1, stage: 'standard',
    name: 'Private V2 composition', description: 'Retain exact private build artifacts; publication unavailable.',
    createdAt: '2026-10-12T00:00:00.000Z', createdBy: 'system',
    config: { artwork: 'none', maxRepairRounds: 0, diagnosis: false, qualityPasses: 0, qualityBar: 1, instructions: '' } };
}
